import { get, has, some } from 'lodash';
import { t } from '@lingui/macro';

export const BEACON_MODES = {
    STEP: 'step',
    NO_STEP: 'nostep',
    SINGLE: 'single',
    DISABLED: 'disable',
};

export function getBeaconModeLabel(mode) {
    switch (mode) {
        case BEACON_MODES.SINGLE:
            return t`Enable For Test`;
        case BEACON_MODES.STEP:
            return t`Enable Per Step`;
        case BEACON_MODES.NO_STEP:
            return t`Enable`;
        case BEACON_MODES.DISABLED:
            return t`Disable`;
        default:
            return mode;
    }
}

/**
 * Derive beacon steps from testType metrics where metricIsStep is true.
 * Mirrors portal/ui's getBeaconsStepsFromTestType().
 */
export function getBeaconStepsFromTestType(testType) {
    const metrics = get(testType, 'metrics', []);
    return metrics
        .filter(Boolean)
        .filter((metric) => metric.metricIsStep)
        .map((metric) => ({
            metricName: metric.name,
            metricTitle: metric.title,
            metricId: metric.id,
            enabled: metric.beaconStep,
        }));
}

/**
 * Ensure each step from the test record has an 'enabled' property.
 * Mirrors portal/ui's getBeaconsStepsFromTest().
 */
export function normalizeTestBeaconSteps(steps) {
    return steps.filter(Boolean).map((step) => {
        if (!has(step, 'enabled')) {
            return { ...step, enabled: false };
        }
        return step;
    });
}

/**
 * Reconcile test steps with current testType metrics.
 * Removes steps whose metricId no longer exists in testType,
 * adds new metrics that appeared, and syncs metricName.
 * Mirrors portal/ui's updateStepsFromTestType().
 */
export function reconcileStepsWithTestType(testSteps, testType) {
    const testTypeSteps = getBeaconStepsFromTestType(testType);
    const remainingTypeSteps = [...testTypeSteps];
    const removedMetricIds = [];

    // Clone all test steps to avoid mutating formData
    const clonedSteps = testSteps.filter(Boolean).map((step) => ({ ...step }));

    clonedSteps.forEach((step) => {
        const typeStepIndex = remainingTypeSteps.findIndex((ts) => ts.metricId === step.metricId);
        if (typeStepIndex === -1) {
            removedMetricIds.push(step.metricId);
        } else {
            const typeStep = remainingTypeSteps[typeStepIndex];
            if (typeStep.metricName !== step.metricName) {
                step.metricName = typeStep.metricName;
            }
            remainingTypeSteps.splice(typeStepIndex, 1);
        }
    });

    const survivingSteps = clonedSteps.filter((step) => !removedMetricIds.includes(step.metricId));
    return [...survivingSteps, ...remainingTypeSteps];
}

/**
 * Compute the resolved beacon steps array from form data and test type.
 * Priority: test record steps > testType-derived steps.
 * Also reconciles with testType to catch metric changes.
 */
export function resolveBeaconSteps(formData, testType, isBttTest) {
    if (!isBttTest) {
        return [];
    }

    const testSteps = get(formData, 'beaconSteps', []);
    if (testSteps.length > 0) {
        const normalized = normalizeTestBeaconSteps(testSteps);
        return reconcileStepsWithTestType(normalized, testType);
    }

    return getBeaconStepsFromTestType(testType);
}

/**
 * Compute the initial beaconMode from API data.
 * Mirrors portal/ui's setupBeaconSteps() initial selection logic.
 */
export function computeInitialBeaconMode(formData, beaconSteps) {
    const beaconPackets = get(formData, 'beaconPackets', false);

    if (!beaconPackets) {
        return BEACON_MODES.DISABLED;
    }

    if (beaconSteps.length > 0 && some(beaconSteps, { enabled: true })) {
        return BEACON_MODES.STEP;
    }

    if (beaconSteps.length > 0) {
        return BEACON_MODES.SINGLE;
    }

    return BEACON_MODES.NO_STEP;
}

/**
 * Determine which dropdown options to show based on testType.
 * Mirrors portal/ui's setupBeaconSteps() filtering logic.
 */
export function getAvailableBeaconModes(isBttTest, beaconSteps) {
    if (!isBttTest) {
        return [BEACON_MODES.NO_STEP, BEACON_MODES.DISABLED];
    }

    if (beaconSteps.length > 0) {
        return [BEACON_MODES.SINGLE, BEACON_MODES.STEP, BEACON_MODES.DISABLED];
    }

    return [BEACON_MODES.NO_STEP, BEACON_MODES.DISABLED];
}
